import { createSelector, type MemoizedSelector } from '@ngrx/store';

import { type CanvasElementId, type ConnectionChain } from '@blaze-runtime/engine-domain';
import {
  chainReferencesWorkflow,
  getInputWorkflowIdForElement,
} from '@blaze-runtime/engine-domain';
import { type PageInfo } from '@blaze-shared/api-contracts-domain';
import {
  type ElementMetaData,
  type ElementMetaDataFeatures,
  type ElementOutputUi,
  type ElementPaginationRequest,
  type ElementQueryState,
  type FormContextCalendar,
  type FormListErrors,
  type FormState,
  type FormStateContainer,
  isRowActionChangePending,
  type RenderElementContext,
  type RenderElementDataRowContext,
  type RowActionChangePending,
  type RuntimePage,
} from '@blaze-shared/elements-domain';
import {
  type BlazeSchema,
  createElementSchemaId,
  type ElementSchema,
  getElementSchema,
  getSchemaById,
  getWorkflowSchemas,
  type RuntimeEventType,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';
import {
  findFirstNodeInWorkflowOfType,
  type Workflow,
  type WorkflowNode,
  type WorkflowStatus,
} from '@blaze-shared/workflow-domain';

import { getEngineStateBase } from '../engine-store';

export const selectBearerToken = createSelector(getEngineStateBase, (state) => state.bearerToken);

export const selectConnections = createSelector(getEngineStateBase, (state) => state.connections);

export const selectConnectionChains = createSelector(
  getEngineStateBase,
  (state) => state.connectionChains,
);

export const selectConnectionChainsByNamespace = (
  ns: string,
): MemoizedSelector<object, ConnectionChain[]> =>
  createSelector(selectConnectionChains, (connectionChains) => connectionChains[ns]);

export const selectIsWorkflowQueued = (workflowId: string): MemoizedSelector<object, boolean> =>
  createSelector(selectConnectionChains, (connectionChains) =>
    Object.values(connectionChains).some((chains) =>
      chains.some((chain) => chainReferencesWorkflow(chain, workflowId)),
    ),
  );

export const selectSchemas = createSelector(getEngineStateBase, (state) => state.schemas);

export const selectDatasetNames = createSelector(selectSchemas, (schemas) => {
  const names: Record<string, string> = {};
  for (const datasetId in schemas) {
    const name = schemas[datasetId]?.name;
    if (name) {
      names[datasetId] = name;
    }
  }
  return names;
});

export const selectInitializedUiElements = createSelector(
  getEngineStateBase,
  (state) => state.initializedUiElements,
);

export const selectIsRunning = createSelector(getEngineStateBase, (state) => state.isRunning);

export const selectIsRuntimeInfoFetched = createSelector(
  getEngineStateBase,
  (state) => state.isRuntimeInfoFetched,
);

export const selectOutputsUi = createSelector(getEngineStateBase, (state) => state.outputsUi);

export const selectOutputUiById = (
  elementId: CanvasElementId,
  eventType: RuntimeEventType | undefined,
): MemoizedSelector<object, ElementOutputUi | undefined> =>
  createSelector(selectOutputsUi, (outputsUi) => {
    const outputKey = eventType ? createElementSchemaId(elementId, eventType) : elementId;
    return outputsUi[outputKey];
  });

export const selectOutputUiWithSchemaById = (
  elementId: CanvasElementId,
  eventType: RuntimeEventType | undefined,
): MemoizedSelector<
  object,
  {
    output: ElementOutputUi | undefined;
    schema: BlazeSchema | ElementSchema | undefined;
  }
> =>
  createSelector(
    selectOutputUiById(elementId, eventType),
    selectSchemaById(elementId),
    (output, schema) => ({ output, schema }),
  );

export const selectOutputUiDataWithSchemaById = (
  elementId: CanvasElementId,
  eventType: RuntimeEventType | undefined,
): MemoizedSelector<object, { data: any; schema: BlazeSchema | ElementSchema | undefined }> =>
  createSelector(
    selectOutputUiById(elementId, eventType),
    selectSchemaById(elementId),
    (outputsUiEntry, schema) => ({ data: outputsUiEntry?.data, schema }),
  );

export const selectOutputUiDataById = (
  elementId: CanvasElementId,
  eventType: RuntimeEventType | undefined,
): MemoizedSelector<object, any> =>
  createSelector(
    selectOutputUiById(elementId, eventType),
    (outputsUiEntry) => outputsUiEntry?.data,
  );

export const selectEventsUi = createSelector(getEngineStateBase, (state) => state.eventsUi);

export const selectWorkflowOutputs = createSelector(
  getEngineStateBase,
  (state) => state.workflowOutputs,
);

export const selectWorkflowOutputsById = (id: string): MemoizedSelector<object, any> =>
  createSelector(selectWorkflowOutputs, (workflowOutputs) => workflowOutputs[id]);

export const selectWorkflowsPagination = createSelector(
  getEngineStateBase,
  (state) => state.workflowsPagination,
);

export const selectWorkflowsPaginationById = (id: string): MemoizedSelector<object, boolean> =>
  createSelector(selectWorkflowsPagination, (paginations) => paginations[id]);

export const selectQueries = createSelector(getEngineStateBase, (state) => state.elementsQuery);

export const selectQueryByElementId = (
  elementId: string,
): MemoizedSelector<object, ElementQueryState | undefined> =>
  createSelector(selectQueries, (queries) => queries[elementId]);

export const selectPendingPaginations = createSelector(
  getEngineStateBase,
  (state) => state.pendingPaginations,
);

export const selectPendingPaginationsByElementId = (
  elementId: string,
): MemoizedSelector<object, ElementPaginationRequest | undefined> =>
  createSelector(selectPendingPaginations, (requests) => requests[elementId]);

export const selectNamespace = createSelector(getEngineStateBase, (state) => state.namespace);

export const selectPendingUi = createSelector(getEngineStateBase, (state) => state.pendingUi);

export const selectRowActionPendingUiByElementId = (
  elementId: CanvasElementId,
): MemoizedSelector<object, RowActionChangePending[]> =>
  createSelector(selectPendingUi, (pendingUi) => {
    return (pendingUi[elementId] || []).filter(isRowActionChangePending);
  });

export const selectIsPendingUiByElementId = (
  elementId: CanvasElementId,
): MemoizedSelector<object, boolean> =>
  createSelector(selectPendingUi, (pendingUi) => pendingUi[elementId]?.length > 0 || false);

export const selectIsCustomerPortal = createSelector(
  getEngineStateBase,
  (state) => state.isCustomerPortal,
);

export const selectLayoutIds = createSelector(getEngineStateBase, (state) => state.layoutIds ?? []);

export const selectPageById = (
  pageId: CanvasElementId,
): MemoizedSelector<object, RuntimePage | undefined> =>
  createSelector(selectRuntimePages, (runtimePages) => runtimePages?.[pageId]);

export const selectPageLayoutUrlById = (
  pageId: CanvasElementId,
): MemoizedSelector<object, string | undefined> =>
  createSelector(selectRuntimePages, (runtimePages) => runtimePages?.[pageId]?.layoutUrl);

export const selectPageId = createSelector(getEngineStateBase, (state) => state.pageId);

export const selectPageIdBySlug = (
  pageSlug: string,
): MemoizedSelector<object, string | undefined> =>
  createSelector(selectRuntimePages, (runtimePages) => {
    for (const pageId in runtimePages) {
      if (runtimePages[pageId]?.slug === pageSlug) {
        return pageId;
      }
    }
    return;
  });

export const selectPageSlugByPageId = (
  pid?: string,
): MemoizedSelector<object, string | undefined> =>
  createSelector(selectRuntimePages, (runtimePages) => {
    if (!pid) {
      return;
    }
    for (const pageId in runtimePages) {
      if (pageId === pid) {
        return runtimePages[pageId]?.slug;
      }
    }
    return;
  });
export const selectRuntimeMode = createSelector(getEngineStateBase, (state) => state.runtimeMode);

export const selectRuntimePages = createSelector(getEngineStateBase, (state) => state.runtimePages);

export const selectPages = createSelector(getEngineStateBase, (state) => state.pages);

export const selectPagesById = (pageId: string): MemoizedSelector<object, PageInfo | undefined> =>
  createSelector(selectPages, (pages) => pages.find((p) => p.id === pageId));

export const selectPublishedUrl = createSelector(
  getEngineStateBase,
  (state) => state.publishedUrl || '',
);

export const selectProjectId = createSelector(getEngineStateBase, (state) => state.projectId);

export const selectProjectName = createSelector(getEngineStateBase, (state) => state.projectName);

export const selectSchemaById = (
  id: string,
  eventType?: RuntimeEventType,
): MemoizedSelector<object, BlazeSchema | ElementSchema | undefined> =>
  createSelector(selectSchemas, (schemas) => {
    if (eventType) {
      const elementSchema = getElementSchema(id, schemas, eventType);
      return elementSchema;
    }
    return getSchemaById(id, schemas);
  });

export const selectWorkflowsSchemas = createSelector(selectSchemas, (schemas) =>
  getWorkflowSchemas(schemas),
);

export const selectWorkflowsSchemaById = (id: string): MemoizedSelector<object, WorkflowSchema> =>
  createSelector(selectWorkflowsSchemas, (schemas) => schemas[id]);

export const selectWorkflowsSchemaWithOutputAndStatusById = (
  id: string,
): MemoizedSelector<object, { output: any; schema: WorkflowSchema; status: WorkflowStatus }> =>
  createSelector(
    selectWorkflowOutputsById(id),
    selectWorkflowsSchemaById(id),
    selectWorkflowsStatusById(id),
    (output, schema, status) => ({ output, schema, status }),
  );

export const selectWorkflows = createSelector(getEngineStateBase, (state) => state.workflows);

export const selectWorkflowsByPageId = (pageId: string): MemoizedSelector<object, Workflow[]> =>
  createSelector(selectWorkflows, (workflows) => workflows.filter((w) => w.pageId === pageId));

export const selectWorkflowsStatus = createSelector(
  getEngineStateBase,
  (state) => state.workflowsStatus,
);

export const selectWorkflowsStatusById = (
  workflowId: string,
): MemoizedSelector<object, WorkflowStatus> =>
  createSelector(selectWorkflowsStatus, (status) => status[workflowId]);

export const selectWorkflowById = (
  workflowId: string,
): MemoizedSelector<object, Workflow | undefined> =>
  createSelector(selectWorkflows, (workflows) => {
    return workflows.find((w) => w.id === workflowId);
  });

export const selectInputWorkflowForElement = (
  elementId: CanvasElementId,
): MemoizedSelector<object, Workflow | undefined> =>
  createSelector(selectConnections, selectWorkflows, (connections, workflows) => {
    const inputWorkflowId = getInputWorkflowIdForElement(connections, elementId);
    return inputWorkflowId ? workflows.find(({ id }) => id === inputWorkflowId) : undefined;
  });

export const selectFirstNodeInWorkflow = <T extends WorkflowNode>(
  workflowId: string,
  predicate: (node?: WorkflowNode) => node is T,
): MemoizedSelector<object, T | undefined> =>
  createSelector(
    selectWorkflowById(workflowId),
    (workflow) => workflow && findFirstNodeInWorkflowOfType(workflow, predicate),
  );

export const selectFormsState = createSelector(getEngineStateBase, (state) => state.forms);

export const selectFormStateById = (
  formElementId: CanvasElementId | undefined,
): MemoizedSelector<object, FormState<FormStateContainer> | undefined> =>
  createSelector(selectFormsState, (state) => (formElementId ? state[formElementId] : undefined));
export const selectFormContextById = (
  formElementId: CanvasElementId | undefined,
): MemoizedSelector<object, FormContextCalendar | undefined> =>
  createSelector(selectFormsState, (state) =>
    formElementId ? state[formElementId]?.context : undefined,
  );
export const selectFormIsSubmittedById = (
  formElementId: CanvasElementId | undefined,
): MemoizedSelector<object, boolean> =>
  createSelector(selectFormStateById(formElementId), (formState) => formState?.submitted ?? false);

export const selectFormStateByIdAndRowId = (
  formElementId: CanvasElementId | undefined,
  rowId: string,
): MemoizedSelector<object, FormListErrors | undefined> =>
  createSelector(
    selectFormStateById(formElementId),
    (formState) => (formState as FormState<'editableList'>)?.errors?.[rowId],
  );

export const selectElementMeta = createSelector(getEngineStateBase, (state) => state.elementsMeta);

export const selectElementMetaById = (
  elementId: CanvasElementId | undefined,
): MemoizedSelector<object, ElementMetaData> =>
  createSelector(
    getEngineStateBase,
    (state) => (elementId ? state.elementsMeta[elementId] : {}) ?? {},
  );

export const selectElementMetaDataFeaturesById = (
  elementId: CanvasElementId,
): MemoizedSelector<object, ElementMetaDataFeatures | undefined> =>
  createSelector(selectElementMetaById(elementId), (meta) => meta.dataFeatures);

export const selectRenderElementContext = (
  elementId: CanvasElementId | undefined,
  rowContext?: RenderElementDataRowContext,
): MemoizedSelector<object, RenderElementContext | undefined> =>
  createSelector(selectElementMetaById(elementId), (meta) => {
    if (!meta.renderContext || !elementId) {
      return undefined;
    }

    const key = rowContext?.rowId !== undefined ? `${elementId}__${rowContext?.rowId}` : elementId;
    return meta.renderContext[key];
  });

export const selectGlobalQuery = createSelector(getEngineStateBase, (state) => state.globalQuery);

export const selectPendingPageCreations = createSelector(
  getEngineStateBase,
  (state) => state.pendingPageCreations,
);

export const selectIsGeneratingAILayout = createSelector(
  selectPendingPageCreations,
  (pendingPageCreations) => Object.values(pendingPageCreations).some((p) => !!p.aiLayout),
);
